import React, { useState } from 'react';

export default function IngredientList({ ingredients, onAdd, onUpdate, onDelete }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  return (
    <div className="space-y-2">
      {ingredients.map((ingredient) => (
        <IngredientRow
          key={ingredient.id}
          ingredient={ingredient}
          isEditing={editingId === ingredient.id}
          onEdit={() => setEditingId(ingredient.id)}
          onSave={(data) => {
            onUpdate(ingredient.id, data);
            setEditingId(null);
          }}
          onCancel={() => setEditingId(null)}
          onDelete={() => onDelete(ingredient.id)}
        />
      ))}

      {showAddForm ? (
        <IngredientForm
          onSave={(data) => {
            onAdd(data);
            setShowAddForm(false);
          }}
          onCancel={() => setShowAddForm(false)}
        />
      ) : (
        <button
          onClick={() => setShowAddForm(true)}
          className="w-full py-2 text-sm text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors"
        >
          + Add Ingredient
        </button>
      )}
    </div>
  );
}

function IngredientRow({ ingredient, isEditing, onEdit, onSave, onCancel, onDelete }) {
  if (isEditing) {
    return (
      <IngredientForm
        ingredient={ingredient}
        onSave={onSave}
        onCancel={onCancel}
      />
    );
  }

  return (
    <div className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 group">
      <span className="text-gray-700 dark:text-gray-300">
        {ingredient.display_text}
      </span>
      <div className="opacity-0 group-hover:opacity-100 flex gap-2 transition-opacity">
        <button
          onClick={onEdit}
          className="text-xs text-gray-500 hover:text-indigo-600"
        >
          Edit
        </button>
        <button
          onClick={onDelete}
          className="text-xs text-gray-500 hover:text-red-600"
        >
          Delete
        </button>
      </div>
    </div>
  );
}

function IngredientForm({ ingredient, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    name: ingredient?.name || '',
    quantity: ingredient?.quantity || '',
    unit: ingredient?.unit || '',
    notes: ingredient?.notes || '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    onSave({
      ...formData,
      quantity: formData.quantity ? parseFloat(formData.quantity) : null,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 space-y-2">
      <div className="flex gap-2">
        <input
          type="number"
          name="quantity"
          value={formData.quantity}
          onChange={handleChange}
          placeholder="Qty"
          step="any"
          className="w-20 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white"
        />
        <input
          type="text"
          name="unit"
          value={formData.unit}
          onChange={handleChange}
          placeholder="Unit"
          className="w-20 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white"
        />
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="Ingredient name"
          required
          className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white"
        />
      </div>
      <input
        type="text"
        name="notes"
        value={formData.notes}
        onChange={handleChange}
        placeholder="Notes (optional)"
        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-1 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white"
      />
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-3 py-1 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700"
        >
          {ingredient ? 'Update' : 'Add'}
        </button>
      </div>
    </form>
  );
}
